import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { OrbindApiError } from '@orbind/api-client'
import { useToast } from '@orbind/ui'
import { api } from '../../lib/api.ts'
import type { Bootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import { useWiMutation } from './api.ts'
import { Dropdown, TONE_COLOR, initialsOf } from './bits.tsx'
import { ListaDePessoas } from './ListaDePessoas.tsx'
import { CatalogIcon } from '../../components/IconPicker.tsx'

/**
 * Controles inline de um FILHO (linha de checklist ou de Filhos): etapa, status
 * e responsável, todos mudáveis ali mesmo ao clicar. As mutações são no item
 * FILHO, mas invalidam o detalhe do PAI (`parentId`) para a lista atualizar.
 */
export function ChildRowControls({
  parentId,
  child,
  bootstrap,
}: {
  parentId: string
  child: {
    id: string
    typeId?: string | null
    flowStepId?: string | null
    status?: { code: string; tone: string } | null
    stage?: { step: string | null; flow: string | null } | null
    members?: { id: string; name: string; avatarUrl?: string | null }[]
    /** Responsável do item — a estrela da lista de pessoas marca aqui. */
    assigneeMemberId?: string | null
  }
  bootstrap: Bootstrap
}) {
  const t = useT()
  const toast = useToast()
  const [aberto, setAberto] = useState<null | 'tipo' | 'status' | 'etapa' | 'resp'>(null)

  const catalogLabel = (row: { code: string; nameTranslationKey: string } | undefined) => {
    if (!row) return '—'
    const l = t(row.nameTranslationKey)
    return l === row.nameTranslationKey ? row.code : l
  }

  const patch = useWiMutation(parentId, async (json: Record<string, string | null>) =>
    api['work-items'][':id'].$patch({ param: { id: child.id }, query: {}, json: json as never }),
  )
  const mudarStatus = useWiMutation(parentId, async (statusId: string) =>
    api['work-items'][':id'].status.$post({ param: { id: child.id }, json: { statusId } }),
  )
  // adiciona/remove um membro do FILHO (toggle)
  const toggleMembro = useWiMutation(parentId, async (memberId: string) =>
    api['work-items'][':id'].members[':memberId'].toggle.$post({ param: { id: child.id, memberId } }),
  )

  const flowNodes = useQuery({
    queryKey: ['flow-nodes'],
    queryFn: async () => (await api.flows.nodes.$get()).json(),
    enabled: aberto === 'etapa',
    retry: false,
  })
  const nos = flowNodes.data?.nodes ?? []
  // tipo do filho + os fluxos que o tipo permite (a etapa inicial obedece o tipo)
  const tipoAtual = bootstrap.workItemTypes.find((tp) => tp.id === child.typeId)
  const flowIdsDoTipo = new Set(((tipoAtual as { flowIds?: string[] })?.flowIds) ?? [])
  // etapas do fluxo atual do filho (ou, sem etapa, os fluxos permitidos pelo tipo)
  const noAtual = child.flowStepId ? nos.find((n) => n.flowStepId === child.flowStepId) : undefined
  const etapasDoFluxo = noAtual ? nos.filter((n) => n.flowId === noAtual.flowId) : []
  const fluxos = [...new Map(nos.map((n) => [n.flowId, n.flowName])).entries()].filter(
    ([fid]) => flowIdsDoTipo.size === 0 || flowIdsDoTipo.has(fid),
  )
  const [fluxoSel, setFluxoSel] = useState<string | null>(null)

  const patchTipo = useWiMutation(parentId, async (typeId: string) =>
    api['work-items'][':id'].$patch({ param: { id: child.id }, query: {}, json: { workItemTypeId: typeId } as never }),
  )

  const membros = bootstrap.members
  /** Quem responde pelo item — mostrado com anel, antes dos membros. */
  const responsavel = child.assigneeMemberId
    ? bootstrap.members.find((m) => m.id === child.assigneeMemberId)
    : undefined
  const statusRow = bootstrap.workItemStatuses.find((s) => s.code === child.status?.code)

  const fecharApos = (fn: () => void) => {
    fn()
    setAberto(null)
  }

  return (
    <div className="wi-cl__ctrls">
      {/* TIPO do item (antes da etapa) — mudável; a etapa inicial segue o tipo */}
      <div className="wi-menuwrap">
        <button
          type="button"
          className="wi-cl__pill"
          title={tipoAtual ? `${catalogLabel(tipoAtual)} — mudar o tipo` : 'Definir o tipo'}
          onClick={() => setAberto(aberto === 'tipo' ? null : 'tipo')}
        >
          <CatalogIcon icon={tipoAtual?.icon} fallback="fas fa-tasks" size={11} />
          <span className="wi-cl__pill-txt">{tipoAtual ? catalogLabel(tipoAtual) : 'Tipo'}</span>
        </button>
        <Dropdown open={aberto === 'tipo'} onClose={() => setAberto(null)} align="left" width={220}>
          {bootstrap.workItemTypes
            .filter((tp) => tp.isActive)
            .map((tp) => (
              <button
                key={tp.id}
                type="button"
                className={`wi-menu__opt${tp.id === child.typeId ? ' wi-menu__opt--on' : ''}`}
                onClick={() => fecharApos(() => patchTipo.mutate(tp.id))}
              >
                <CatalogIcon icon={tp.icon} fallback="fas fa-tasks" size={11} />
                {catalogLabel(tp)}
                <span style={{ flex: 1 }} />
                <i className={`fas fa-check wi-menu__check${tp.id === child.typeId ? '' : ' wi-menu__check--off'}`} aria-hidden />
              </button>
            ))}
        </Dropdown>
      </div>

      {/* ETAPA */}
      <div className="wi-menuwrap">
        <button
          type="button"
          className="wi-cl__pill wi-cl__pill--etapa"
          title={child.stage?.step ? `${child.stage.step} · ${child.stage.flow} — mudar etapa` : 'Definir etapa'}
          onClick={() => setAberto(aberto === 'etapa' ? null : 'etapa')}
        >
          <i className="fas fa-map-signs" aria-hidden />
          <span className="wi-cl__pill-txt">{child.stage?.step ?? 'Etapa'}</span>
        </button>
        <Dropdown open={aberto === 'etapa'} onClose={() => setAberto(null)} align="left" width={230}>
          {child.flowStepId ? (
            etapasDoFluxo.length ? (
              etapasDoFluxo.map((n) => (
                <button
                  key={n.flowStepId}
                  type="button"
                  className="wi-menu__opt"
                  onClick={() => fecharApos(() => patch.mutate({ flowStepId: n.flowStepId }))}
                >
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: n.stepColor ?? 'var(--ob-primary)', flex: 'none' }} aria-hidden />
                  {n.stepName}
                </button>
              ))
            ) : (
              <p className="wi-cl__menuvazio">Carregando…</p>
            )
          ) : fluxoSel ? (
            <>
              <button type="button" className="wi-menu__opt" onClick={() => setFluxoSel(null)}>
                <i className="fas fa-arrow-left" aria-hidden style={{ fontSize: 10, width: 14 }} /> Trocar fluxo
              </button>
              {nos
                .filter((n) => n.flowId === fluxoSel)
                .map((n) => (
                  <button
                    key={n.flowStepId}
                    type="button"
                    className="wi-menu__opt"
                    onClick={() => fecharApos(() => patch.mutate({ flowStepId: n.flowStepId }))}
                  >
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: n.stepColor ?? 'var(--ob-primary)', flex: 'none' }} aria-hidden />
                    {n.stepName}
                  </button>
                ))}
            </>
          ) : fluxos.length ? (
            fluxos.map(([fid, fname]) => (
              <button key={fid} type="button" className="wi-menu__opt" onClick={() => setFluxoSel(fid)}>
                <i className="fas fa-route" aria-hidden style={{ fontSize: 10, width: 14, color: 'var(--ob-primary)' }} />
                {fname}
              </button>
            ))
          ) : (
            <p className="wi-cl__menuvazio">Nenhum fluxo disponível.</p>
          )}
        </Dropdown>
      </div>

      {/* STATUS */}
      <div className="wi-menuwrap">
        <button
          type="button"
          className="wi-cl__pill"
          title="Mudar status"
          onClick={() => setAberto(aberto === 'status' ? null : 'status')}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: TONE_COLOR[child.status?.tone ?? 'neutral'], flex: 'none' }} aria-hidden />
          <span className="wi-cl__pill-txt">{statusRow ? catalogLabel(statusRow) : child.status?.code ?? 'Status'}</span>
        </button>
        <Dropdown open={aberto === 'status'} onClose={() => setAberto(null)} align="left" width={210}>
          {bootstrap.workItemStatuses
            .filter((s) => s.isActive)
            .map((s) => (
              <button
                key={s.id}
                type="button"
                className="wi-menu__opt"
                onClick={() =>
                  fecharApos(() =>
                    mudarStatus.mutate(s.id, {
                      onError: (e) =>
                        toast.push({
                          tone: e instanceof OrbindApiError && e.code === 'completion_blocked' ? 'warning' : 'danger',
                          message:
                            e instanceof OrbindApiError && e.code === 'completion_blocked'
                              ? 'Conclua os critérios obrigatórios do filho antes de finalizá-lo.'
                              : e instanceof Error
                                ? e.message
                                : 'Falha ao mudar o status.',
                        }),
                    }),
                  )
                }
              >
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: TONE_COLOR[s.tone], flex: 'none' }} aria-hidden />
                {catalogLabel(s)}
              </button>
            ))}
        </Dropdown>
      </div>

      {/* MEMBROS do filho: avatares + "+" para adicionar/remover, e a estrela
          que define o RESPONSÁVEL (2026-09-21). A linha de checklist já
          convertida mostra estes controles, não os da linha: sem a estrela
          aqui, a opção simplesmente não existia para ela. */}
      <div className="wi-menuwrap wi-cl__membros">
        {responsavel ? (
          <span
            className="wi-chip-member wi-chip-member--responsavel"
            style={{ width: 24, height: 24, fontSize: 9 }}
            title={`${responsavel.name} — responsável`}
          >
            {initialsOf(responsavel.name)}
          </span>
        ) : null}
        {(child.members ?? []).map((m) => (
          <span key={m.id} className="wi-chip-member" style={{ width: 24, height: 24, fontSize: 9 }} title={m.name}>
            {initialsOf(m.name)}
          </span>
        ))}
        <button
          type="button"
          className="wi-cl__resp"
          title="Adicionar ou remover membros do filho"
          aria-label="Membros do filho"
          onClick={() => setAberto(aberto === 'resp' ? null : 'resp')}
        >
          <span className="wi-cl__resp-vazio" aria-hidden>
            <i className="fas fa-user-plus" style={{ fontSize: 10 }} />
          </span>
        </button>
        <Dropdown open={aberto === 'resp'} onClose={() => setAberto(null)} align="left" width={240}>
          <ListaDePessoas
            pessoas={membros}
            ehMembro={(id) => (child.members ?? []).some((x) => x.id === id)}
            responsavelId={child.assigneeMemberId ?? null}
            onAlternarMembro={(id) => toggleMembro.mutate(id)}
            onDefinirResponsavel={(id) => patch.mutate({ assigneeMemberId: id })}
            onFechar={() => setAberto(null)}
          />
        </Dropdown>
      </div>
    </div>
  )
}
